import { EmailService } from '../services/emailService';

// Test email addresses
const TEST_EMAILS = [
  'ethienne.s@itpac.co.za',
  'ethiennesmit@icloud.com'
];

// Sample donation data for testing
const createTestDonationData = (email: string, testNumber: number) => ({
  firstName: 'Test',
  lastName: `User ${testNumber}`,
  email: email,
  cellNumber: '+27123456789',
  amount: 150,
  donationType: 'one-time' as const
});

/**
 * Test email sending to specific addresses
 */
export const testEmailSending = async (): Promise<void> => {
  console.log('🧪 Starting Email Test...\n');
  
  const results: Array<{
    email: string;
    success: boolean;
    error?: string;
    duration?: number;
  }> = [];

  for (let i = 0; i < TEST_EMAILS.length; i++) {
    const email = TEST_EMAILS[i];
    const testData = createTestDonationData(email, i + 1);
    
    console.log(`📧 Testing email ${i + 1}/${TEST_EMAILS.length}: ${email}`);
    
    const startTime = Date.now();
    
    try {
      await EmailService.sendDonationConfirmation(
        testData,
        `TEST-TXN-${Date.now()}-${i + 1}`
      );
      
      const duration = Date.now() - startTime;
      
      results.push({
        email,
        success: true,
        duration
      });
      
      console.log(`✅ Success! Email sent to ${email} (${duration}ms)\n`);
      
    } catch (error) {
      const duration = Date.now() - startTime;
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      
      // Log detailed error information
      console.error(`❌ Detailed error for ${email}:`, error);
      
      results.push({
        email,
        success: false,
        error: errorMessage,
        duration
      });
      
      console.log(`❌ Failed! Email to ${email}: ${errorMessage} (${duration}ms)\n`);
    }
  }
  
  // Print summary
  console.log('📊 Test Results Summary:');
  console.log('========================');
  
  const successful = results.filter(r => r.success);
  const failed = results.filter(r => !r.success);
  
  console.log(`✅ Successful: ${successful.length}/${results.length}`);
  console.log(`❌ Failed: ${failed.length}/${results.length}`);
  
  if (successful.length > 0) {
    const avgDuration = successful.reduce((sum, r) => sum + (r.duration || 0), 0) / successful.length;
    console.log(`⏱️  Average send time: ${Math.round(avgDuration)}ms`);
  }
  
  if (failed.length > 0) {
    console.log('\n❌ Failed emails:');
    failed.forEach(result => {
      console.log(`   • ${result.email}: ${result.error}`);
    });
  }
  
  console.log('\n🏁 Email test completed!');
};

/**
 * Test individual email
 */
export const testSingleEmail = async (email: string): Promise<boolean> => {
  console.log(`🧪 Testing single email: ${email}`);
  
  const testData = createTestDonationData(email, 1);
  
  try {
    await EmailService.sendDonationConfirmation(
      testData,
      `SINGLE-TEST-${Date.now()}`
    );
    
    console.log(`✅ Success! Email sent to ${email}`);
    return true;
    
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Unknown error';
    console.log(`❌ Failed! Email to ${email}: ${errorMessage}`);
    return false;
  }
};

/**
 * Test email configuration
 */
export const testEmailConfig = (): void => {
  console.log('🔧 Testing Email Configuration...\n');
  
  try {
    // Import the config function
    const { getEmailConfig } = require('../config/email.config');
    const config = getEmailConfig();
    
    console.log('📋 Current Configuration:');
    console.log(`   Provider: ${config.provider}`);
    console.log(`   From Email: ${config.fromEmail}`);
    console.log(`   SMTP Host: ${config.smtp?.host || 'N/A'}`);
    console.log(`   SMTP Port: ${config.smtp?.port || 'N/A'}`);
    console.log(`   SMTP User: ${config.smtp?.user || 'N/A'}`);
    console.log(`   SMTP Pass: ${config.smtp?.pass ? '***SET***' : 'Not set'}`);
    console.log(`   SMTP Secure: ${config.smtp?.secure || 'N/A'}`);
    console.log(`   API Key: ${config.apiKey ? '***SET***' : 'Not set'}`);
    
    // Check if configuration is valid
    if (config.provider === 'smtp2go') {
      if (config.smtp?.user && config.smtp?.pass) {
        console.log('\n✅ SMTP2GO SMTP configuration appears valid');
      } else {
        console.log('\n❌ SMTP2GO SMTP configuration missing credentials');
      }
      
      if (config.apiKey) {
        console.log('✅ SMTP2GO API key is available');
      } else {
        console.log('⚠️ SMTP2GO API key not set - will use SMTP method');
      }
    }
    
    // Check if window.EMAIL_CONFIG is available
    if (typeof window !== 'undefined' && (window as any).EMAIL_CONFIG) {
      console.log('✅ Window EMAIL_CONFIG is available');
      console.log('📋 Window Config:', JSON.stringify((window as any).EMAIL_CONFIG, null, 2));
    } else {
      console.log('⚠️ Window EMAIL_CONFIG not available - using environment/defaults');
    }
    
    console.log('\n🏁 Configuration test completed!');
    
  } catch (error) {
    console.log('❌ Error testing configuration:', error);
  }
};
